import { JSX, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import AlertBanner from "lib/components/alert/AlertBanner";
import AlertMessage from "lib/components/alert/AlertMessage";
import Prompt from "lib/components/prompt/Prompt";
import useHistory from "lib/history/useHistory";
import ListInfoProvider from "lib/list/ListInfoProvider";
import TableActionbar from "lib/ui/table/TableActionbar";

import {
	ResponseState,
	ResponseStateType,
	mergeState
} from "utils/fetch/FetchResponse";
import useFetch from "utils/fetch/useFetch";
import useGet from "utils/fetch/useGet";
import { DeleteEvent, Event, EventGroup, MoveEvent } from "utils/history/Event";

import FormFieldTable from "core/field/form/FormFieldTable";

import BlueprintLibraryInfo from "core/blueprint/base/BlueprintLibraryInfo";
import { Right } from "core/blueprint/base/BlueprintService";
import EditActionbar from "core/blueprint/competencelist/EditActionbar";

import InformationBlueprintClient from "./InformationBlueprintClient";
import { InformationBlueprint } from "./InformationBlueprintTypes";

export type InformationBlueprintPageProps = {
	id: number;
};

export default function InformationBlueprintPage(
	props: Readonly<InformationBlueprintPageProps>
): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const info = useGet(InformationBlueprintClient.getInfo(props.id));
	const contentResponse = useGet(
		InformationBlueprintClient.getContent(props.id)
	);

	const status: ResponseStateType = useMemo(
		() => mergeState(info.response, contentResponse.response),
		[info.response.state, contentResponse.response.state]
	);

	const { call } = useFetch();

	const history = useHistory<InformationBlueprint>({
		initialState: { fields: [] }
	});

	const [message, setMessage] = useState<AlertMessage>();

	useEffect(() => {
		if (contentResponse.response.state !== ResponseState.SUCCESS) {
			return;
		}

		const data = contentResponse.response.data;
		history.reset(data);
	}, [contentResponse.response]);

	const save = useCallback((): Promise<void> => {
		setMessage(undefined);

		if (!history.hasPrevious) {
			return Promise.resolve();
		}

		const patches = history.actions.flatMap(action => action.patches());

		return call(InformationBlueprintClient.updateContent(props.id, patches))
			.then(() => {
				setMessage(
					AlertMessage.success(
						t("saveDocumentSuccess", {
							document: "informationBlueprint_one"
						})
					)
				);
				contentResponse.refresh();
			})
			.catch(() =>
				setMessage(
					AlertMessage.error(
						t("saveDocumentFailure", {
							document: "informationBlueprint_one"
						})
					)
				)
			);
	}, [history, props.id]);

	const close = useCallback((): void => {
		navigate("/blueprints");
	}, [navigate]);

	const handleMoveUp = useCallback(
		(keys: Array<string>): void => {
			const events: Event[] = [];
			for (const [index, field] of history.state.fields.entries()) {
				if (keys.includes(field.uuid)) {
					events.push(
						new MoveEvent(
							`/fields/${index}`,
							`/fields/${index - 1}`
						)
					);
				}
			}
			const event = new EventGroup(events);
			history.perform(event.toAction());
		},
		[history]
	);

	const handleMoveDown = useCallback(
		(keys: Array<string>): void => {
			const events: Event[] = [];
			for (const [index, field] of history.state.fields.entries()) {
				if (keys.includes(field.uuid)) {
					events.unshift(
						new MoveEvent(
							`/fields/${index}`,
							`/fields/${index + 1}`
						)
					);
				}
			}
			const event = new EventGroup(events);
			history.perform(event.toAction());
		},
		[history]
	);

	const handleDelete = useCallback(
		(keys: Array<string>): void => {
			const events: Event[] = [];
			for (const [index, field] of history.state.fields.entries()) {
				if (keys.includes(field.uuid)) {
					events.unshift(new DeleteEvent(`/fields/${index}`, field));
				}
			}
			const event = new EventGroup(events);
			history.perform(event.toAction());
		},
		[history]
	);

	const updateField = useCallback(
		(event: Event): void => {
			event.applyContext("/fields");
			history.perform(event.toAction());
		},
		[history]
	);

	const canEdit = useCallback((): boolean => {
		if (info.response.state !== ResponseState.SUCCESS) {
			return false;
		}

		return info.response.data.rights.includes(Right.WRITE) ?? false;
	}, [info]);

	const handleUpdateBlueprint = useCallback((): void => {
		info.refresh();
		contentResponse.refresh();
	}, [info.refresh, contentResponse.refresh]);

	return (
		<>
			{status === ResponseState.LOADING && (
				<Box display="flex" justifyContent="center">
					<CircularProgress />
				</Box>
			)}

			{status === ResponseState.ERROR && (
				<AlertBanner
					alert={AlertMessage.error(
						t("loadDocumentError", {
							document: "informationBlueprint_one"
						})
					)}
				/>
			)}

			{info.response.state === ResponseState.SUCCESS &&
				contentResponse.response.state === ResponseState.SUCCESS && (
					<>
						{message && <AlertBanner alert={message} />}
						<Typography variant="h1">
							{info.response.data.name}
						</Typography>

						{info.response.data.sourceBlueprintId !== null &&
							info.response.data.sourceBlueprintVersion !==
								null && (
								<BlueprintLibraryInfo
									id={props.id}
									enabled={
										info?.response.data.rights.includes(
											Right.ADMIN
										) ?? false
									}
									blueprintId={
										info.response.data.sourceBlueprintId
									}
									blueprintVersion={
										info.response.data
											.sourceBlueprintVersion
									}
									onUpdate={handleUpdateBlueprint}
								/>
							)}

						<EditActionbar
							undoEnabled={history.hasPrevious}
							redoEnabled={history.hasNext}
							saveEnabled={history.hasPrevious}
							onUndo={history.undo}
							onRedo={history.redo}
							onSave={save}
							onClose={close}
						/>

						<ListInfoProvider
							keys={history.state.fields.map(f => f.uuid)}
							isEnabled={canEdit}>
							<TableActionbar
								onMoveUp={handleMoveUp}
								onMoveDown={handleMoveDown}
								onDelete={handleDelete}
							/>

							<FormFieldTable
								elements={history.state.fields}
								editable={canEdit()}
								onChange={updateField}
								onInsert={updateField}
							/>
						</ListInfoProvider>

						<Prompt
							when={history.hasPrevious}
							message={t("exitDocumentMessage")}
						/>
					</>
				)}
		</>
	);
}
